import React from "react";

import { AiPart, PickType } from "@intelligems/ig-types";
import {
  Tool,
  ToolContent,
  ToolHeader,
} from "@src/components/ai-elements/tool";
import { AssistantText } from "./AssistantText";
import { WithDebugMessages } from "./WithDebugMessages";
import { getPrettyToolName } from "@src/features/ai/utils/tool-constants";
import { cn } from "@src/lib/utils";
import {
  resolveSlot,
  useExecutionPanelSlot,
  useExecutionPanelSlotProps,
  useRenderToolContent,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { readRequestUserInputOutput } from "./requestUserInputOutput";
import { type ToolBlock } from "./toolContent";

type ToolCallMessageProps = {
  item: FlattenedPart;
  part: PickType<AiPart, "dynamic-tool">;
} & MessageFlags;

export const DynamicTool = ({
  item,
  showToolCallOutput,
  toolCallDefaultExpanded,
  disableResponseWordByWordLoading,
  part,
  debug,
  layoutDensity,
}: ToolCallMessageProps) => {
  const getSlotProps = useExecutionPanelSlotProps();
  const getSlot = useExecutionPanelSlot();
  const renderToolContent = useRenderToolContent();
  const card = getSlotProps("toolCard");

  const { toolName, input, output, state } = part;

  // Steps a tool reported about itself. Independent of `showToolCallOutput`:
  // that flag is about exposing a tool's raw result, and these are the tool's
  // own account of what it did — a chat can want the second without the first.
  // Absent for every tool that reports nothing, which is all of them unless
  // they opt in, so no existing card's appearance moves.
  const steps = part.steps ?? [];
  const showOutput = debug || showToolCallOutput;
  const defaultOpen = toolCallDefaultExpanded ?? state !== "input-streaming";
  const isCompact = layoutDensity === "compact";

  // Steps open the section on their own, so the blocks inside it that belong to
  // the raw result keep their own `showOutput` guard below.
  const showToolDetails =
    steps.length > 0 ||
    (showOutput &&
      (input !== undefined ||
        state === "output-available" ||
        state === "output-error"));

  if (!part) {
    return null;
  }

  const inputRequirement =
    toolName === "request-user-input" && part.state === "output-available"
      ? readRequestUserInputOutput(part.output)
      : undefined;
  if (inputRequirement) {
    return (
      <AssistantText
        item={item}
        part={{
          type: "text",
          text: `${debug ? "⚙️ " : ""}${inputRequirement.prompt}`,
          timestamp: Date.now(),
        }}
        debug={debug}
        disableResponseWordByWordLoading={disableResponseWordByWordLoading}
        layoutDensity={layoutDensity}
      />
    );
  }

  // Which sections apply is the panel's decision; how they are drawn may not
  // be. A surface that sets `renderToolContent` is handed the list and can
  // reorder it, merge it, or replace one section and hand the rest back to
  // `renderToolBlock`.
  const blocks: ToolBlock[] = [];
  if (steps.length > 0) {
    blocks.push({ type: "steps", steps });
  }
  if (showOutput && input !== undefined) {
    blocks.push({ type: "input", input });
  }
  if (
    showOutput &&
    (state === "output-available" || state === "output-error")
  ) {
    blocks.push({ type: "output", output, errorText: part.errorText });
  }
  const content = renderToolContent(blocks, { part, compact: isCompact });

  return (
    <WithDebugMessages item={item} part={part} debug={debug}>
      <Tool
        className={cn(isCompact && "mb-2 rounded-sm", card?.className)}
        defaultOpen={defaultOpen}
      >
        <ToolHeader
          className={cn(
            isCompact && "gap-2 px-2.5 py-2",
            getSlotProps("toolCardHeader")?.className
          )}
          compact={isCompact}
          type={`tool-${toolName}`}
          title={getPrettyToolName(toolName)}
          state={part.state}
          isExpandable={showToolDetails}
          icon={resolveSlot(getSlot("toolCardIcon"), undefined)}
          status={resolveSlot(getSlot("toolCardStatus"), {
            state,
            compact: isCompact,
          })}
        />
        {showToolDetails ? <ToolContent>{content}</ToolContent> : null}
      </Tool>
    </WithDebugMessages>
  );
};
